import Button from "components/common/Button";
import CustomTable from "components/common/CustomTable";
import CustomToast from "components/common/CustomToast";
import Dropdown from "components/common/Dropdown";
import SynthTooltip from "components/common/SynthTooltip";
import TabsContainer from "components/common/TabsContainer";
import VendorAvatar from "components/common/VendorAvatar";
import { formatMoney } from "helpers";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { truncate } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { chatActions } from "redux/Ldb/actions";

type SalesTargetTableProps = {
    salesTarget?: { [key: string]: any }[];
    summary?: string;
    threadId: string;
    messageId: string;
};

const SalesTargetTable = ({ salesTarget, summary, threadId, messageId }: SalesTargetTableProps) => {
    const dispatch = useAppDispatch();
    const [activeTab, setActiveTab] = useState(0);
    const [productFilter, setProductFilter] = useState<string>("");
    const [channelFilter, setChannelFilter] = useState<string>("");
    const [tableData2, setTableData2] = useState<any>([]);
    const [activeMonth, setActiveMonth] = useState<string>("");
    // const [months, setMonths] = useState<string[]>([]);
    // const [savingAll, setSavingAll] = useState<boolean>(false);
    const [savingId, setSavingId] = useState<{
        id?: string;
        channel?: boolean;
    }>({
        id: "",
        channel: false,
    });
    const [salesTargetForecast, setSalesTargetForecast] = useState<any>([]);
    const [targetSavedPerTable, setTargetSavedPerTable] = useState<any>([]);
    const [savingTargetForcastId, setSavingTargetForcastId] = useState<{
        id?: string;
        channel?: string;
    }>({
        id: "",
        channel: "",
    });

    const {
        createForecastSuccess,
        creatingForecast,
        getSalesTargetForecastSuccess,
        saveTargetForecastSuccess,
        savingTargetForecast,
    } = useAppSelector((state) => state.chat);

    const handleCreateSalesTarget = (data) => {
        const body = {
            salesTargets: data,
            agentMetadata: {
                threadId: threadId,
                messageId: messageId,
            },
        };
        dispatch(chatActions.createForecast(body));
    };

    const handleSaveTargetProducts = (data) => {
        const body = {
            threadId: threadId,
            messageId: messageId,
            forecastConfigs: [data],
            // string of ids
        };
        dispatch(chatActions.saveTargetForecast(body));
    };

    useEffect(() => {
        if (Boolean(saveTargetForecastSuccess)) {
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title={"Forecast value set"}
                    message={
                        "Your selected value has been applied as the forecast for this product."
                    }
                    type={"success"}
                />
            ));
            dispatch(chatActions.getSalesTargetForecast(threadId));
            dispatch(chatActions.resetSaveTargetForecastSuccess());
        }
    }, [saveTargetForecastSuccess, dispatch, threadId, savingId]);

    useEffect(() => {
        if (Boolean(createForecastSuccess)) {
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title={savingId ? "Sales target saved" : "Sales targets saved for all products"}
                    message={
                        savingId
                            ? "The sales target for this product has been saved successfully."
                            : "Your sales targets have been applied across all products in this list."
                    }
                    type={"success"}
                />
            ));
            dispatch(chatActions.getSalesTargetForecast(threadId));
            dispatch(chatActions.resetCreateForecastSuccess());
        }
    }, [createForecastSuccess, dispatch, threadId, savingId]);

    function groupProducts(salesTarget: { [key: string]: any }[]) {
        const groupedObject = salesTarget.reduce((acc, product) => {
            const splitDate = product.date.split("-");
            const key = `${splitDate[0]}-${splitDate[1]}`; //
            if (!acc[key]) {
                acc[key] = {
                    date: `${splitDate[0]}-${splitDate[1]}`,
                    monthYear: moment(product.date).format("MMM YYYY"),
                    channel: product?.channel,
                    products: [],
                };
            }
            acc[key].products.push(product);
            return acc;
        }, {});

        const result = Object.values(groupedObject);
        const monthYear = Array.from(new Set(result.map((item: any) => item.monthYear)));
        const allProducts = result.flatMap((item: any) => item.products);
        const uniqueProductNames = Array.from(
            new Set(allProducts.map((item: any) => item?.productName))
        );
        const channels = Array.from(new Set(allProducts.map((item: any) => item.channel)));
        const uniqueMonths = Array.from(new Set(monthYear));
        return {
            channels: channels,
            monthYear: monthYear,
            products: result,
            productArray: uniqueProductNames,
            uniqueMonths: uniqueMonths,
        };
    }

    const grouped = useMemo(() => {
        if (!salesTarget || salesTarget?.length === 0) return null;
        return groupProducts(salesTarget);
    }, [salesTarget]);

    const months = useMemo(() => grouped?.uniqueMonths ?? [], [grouped]);

    useEffect(() => {
        if (months?.length > 0 && activeMonth === "") {
            setActiveMonth(months[0]);
        }
    }, [months]);

    useEffect(() => {
        const monthProduct = grouped?.products?.find((item) => item.monthYear === activeMonth);
        if (productFilter || channelFilter) {
            const filteredProducts = monthProduct?.products?.filter((item) =>
                productFilter && channelFilter
                    ? item.productName === productFilter && item.channel === channelFilter
                    : productFilter
                    ? item.productName === productFilter
                    : channelFilter && item.channel === channelFilter
            );
            setTableData2(filteredProducts?.length > 0 ? filteredProducts : []);
        } else {
            setTableData2(monthProduct?.products?.length > 0 ? monthProduct?.products : []);
        }
    }, [activeMonth, productFilter, channelFilter, grouped]);

    useEffect(() => {
        if (threadId) {
            dispatch(chatActions.getSalesTargetForecast(threadId));
        }
    }, [threadId, dispatch]);

    useEffect(() => {
        if (Boolean(getSalesTargetForecastSuccess)) {
            setSalesTargetForecast(getSalesTargetForecastSuccess);
            dispatch(chatActions.resetGetSalesTargetForecastSuccess());
        }
    }, [getSalesTargetForecastSuccess, dispatch]);

    useEffect(() => {
        const date = moment(activeMonth).format("MM-YYYY");

        const isSaved2 = salesTargetForecast?.find((item) => item?.messageId === messageId);
        //add productName from the backend
        const isSaved = isSaved2?.salesTarget?.filter(
            (target) =>
                target?.date?.startsWith(`${date?.split("-")[1]}-${date?.split("-")[0]}`) &&
                (channelFilter && productFilter
                    ? target?.channel?.toLowerCase() === channelFilter?.toLowerCase() &&
                      target?.inventory?.productName?.toLowerCase() === productFilter?.toLowerCase()
                    : productFilter
                    ? target?.inventory?.productName?.toLowerCase() === productFilter?.toLowerCase()
                    : channelFilter
                    ? target?.channel?.toLowerCase() === channelFilter?.toLowerCase()
                    : true)
        );
        setTargetSavedPerTable(isSaved);
    }, [salesTargetForecast, activeMonth, tableData2, messageId, productFilter, channelFilter]);

    const tableHeader = [
        { title: "Product", widthClass: "w-[230px]" },
        { title: "Channel", widthClass: "w-[100px]" },
        { title: "Forecasted unit", widthClass: "w-[150px]" },
        { title: "Action", widthClass: "w-[350px]" },
    ];

    const tableBody = tableData2?.map((item, idx) => {
        const isSaved = salesTargetForecast
            ?.find((item) => item?.messageId === messageId)
            ?.salesTarget?.find(
                (target) =>
                    target?.inventoryId === item?.inventoryId &&
                    target?.channel === item?.channel &&
                    target?.date?.startsWith(
                        `${item?.date?.split("-")[0]}-${item?.date?.split("-")[1]}`
                    )
            );
        const isSavedTargetForecast = salesTargetForecast
            ?.find((item) => item?.messageId === messageId)
            ?.forecastConfigs?.find(
                (savedForecast) =>
                    savedForecast?.inventoryId === item?.inventoryId &&
                    savedForecast?.channel === item?.channel &&
                    savedForecast?.date?.startsWith(
                        `${item?.date?.split("-")[0]}-${item?.date?.split("-")[1]}`
                    )
            );

        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`py-3 pr-3 border-slate-100 text-sm text-gm-50 font-rocGroteskMedium `}
                    >
                        <div className="flex items-center gap-2">
                            {item?.inventory?.productImageDetails?.productAvatar ? (
                                <VendorAvatar
                                    name={item?.inventory?.productName}
                                    imageSrc={item?.inventory?.productImageDetails?.productAvatar}
                                    size={32}
                                    containerClassname="!rounded-none !border !bg-white"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                    <i className="ri-box-2-fill text-lg text-slate-500"></i>
                                </div>
                            )}
                            <div>
                                <a id={`name-${idx}`}>
                                    <span>
                                        {truncate(item?.inventory?.productName, { length: 20 })}
                                    </span>
                                    <p className=" text-slate-500">SKU: {item?.inventory?.sku}</p>
                                </a>
                                <Tooltip
                                    anchorSelect={`#name-${idx}`}
                                    place={"right"}
                                    content={item?.inventory?.productName}
                                    style={{
                                        maxWidth: "250px",
                                        zIndex: 99999,
                                        background: "#fff",
                                        borderRadius: "6px",
                                        border: "1px solid #E2E8F0",
                                        color: "#1E293B",
                                        boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ),
                widthClass: "!border-b",
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-gm-50 font-rocGroteskMedium `}
                    >
                        <p className="">{item?.channel || ""}</p>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-gm-50 font-rocGroteskMedium `}
                    >
                        <div className="flex items-center gap-2">
                            <i className="ri-sparkling-fill text-sm text-[#56B648]"></i>
                            <p className="">
                                {formatMoney().format(item?.quantity)}{" "}
                                {item?.inventory?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
                widthClass: "!border-b",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm text-gm-50 font-rocGroteskMedium flex gap-2`}
                    >
                        {!isSaved ? (
                            <SynthTooltip
                                divId={`save-target-table-${idx}`}
                                position={"top"}
                                icon={
                                    <Button
                                        btnText={`Save target`}
                                        btnClassname="!bg-[#F4F5F7] !w-fit !text-gm-50 !text-[13px] !whitespace-nowrap !px-2 !py-1 rounded-[5px] text-gm-50 flex-shrink-0"
                                        icon={<i className="ri-add-line text-base text-gm-50"></i>}
                                        isLoading={
                                            creatingForecast &&
                                            savingId?.id === item?.inventoryId &&
                                            savingId?.channel === item?.channel
                                        }
                                        onClick={() => {
                                            setSavingId({
                                                id: item?.inventoryId,
                                                channel: item?.channel,
                                            });
                                            const body = [
                                                {
                                                    inventoryId: item?.inventoryId,
                                                    channel: item?.channel,
                                                    forecastedUnits: item?.quantity,
                                                    targetMonth: item?.date?.split("-")[1],
                                                    targetYear: item?.date?.split("-")[0],
                                                },
                                            ];
                                            handleCreateSalesTarget(body);
                                        }}
                                    />
                                }
                                content="Save this sales target for this specific product"
                                styling={{
                                    width: "200px",
                                    background: "#142837",
                                    borderRadius: "6px",
                                }}
                                contentClass="text-xs text-white"
                            />
                        ) : (
                            <Button
                                btnText={`Target saved `}
                                btnClassname="!bg-[#0AAD0A1A] !w-fit !text-gm-50 !text-[13px] !whitespace-nowrap !px-2 !py-1 rounded-[5px] text-[#0AAD0A] !cursor-default flex-shrink-0"
                                icon={<i className="ri-check-double-fill text-[#0AAD0A] "></i>}
                                type="button"
                            />
                        )}

                        {!isSavedTargetForecast ? (
                            <SynthTooltip
                                divId={`save-forecast-${idx}`}
                                position={"top"}
                                icon={
                                    <Button
                                        btnText={`Set as forecast value`}
                                        btnClassname="!bg-[#F4F5F7] !w-fit !text-gm-50 !text-[13px] !whitespace-nowrap !px-4 !py-1 rounded-[5px] text-gm-50"
                                        icon={<i className="ri-add-line text-base text-gm-50"></i>}
                                        isLoading={
                                            savingTargetForecast &&
                                            savingTargetForcastId?.id === item?.inventoryId &&
                                            savingTargetForcastId?.channel === item?.channel
                                        }
                                        onClick={() => {
                                            setSavingTargetForcastId({
                                                id: item?.inventoryId,
                                                channel: item?.channel,
                                            });
                                            handleSaveTargetProducts({
                                                inventoryId: item?.inventoryId,
                                                channel: item?.channel,
                                                quantity: item?.quantity,
                                                targetMonth: item?.date?.split("-")[1],
                                                targetYear: item?.date?.split("-")[0],
                                            });
                                        }}
                                    />
                                }
                                content="This value will be been applied as the forecast for this product."
                                styling={{
                                    width: "200px",
                                    background: "#142837",
                                    borderRadius: "6px",
                                }}
                                contentClass="text-xs text-white"
                            />
                        ) : (
                            <Button
                                btnText={`Forecast value set `}
                                btnClassname="!bg-[#0AAD0A1A] !w-fit !text-gm-50 !text-[13px] !whitespace-nowrap !px-4 !py-1 rounded-[5px] text-[#0AAD0A] !cursor-default"
                                icon={<i className="ri-check-double-fill text-[#0AAD0A] "></i>}
                                type="button"
                            />
                        )}
                    </div>
                ),
                widthClass: "!border-b",
            },
        ];
    });

    return (
        <div className="relative border border-slate-200 rounded-xl py-[15px]  lg:mt-[16px]">
            <div className="lg:flex justify-between px-2 lg:px-5">
                <div className="flex items-center space-x-6">
                    <p className="text-[#303B41] font-rocGroteskMedium text-lg mb-2">
                        Sales target output
                    </p>
                </div>
            </div>

            <div className="flex items-center space-x-2.5 px-2 lg:px-5">
                <Dropdown
                    handleChange={(name, value) => setProductFilter(value)}
                    // search={
                    //     <div ref={warehouseRef} className="">
                    //         <TextInput
                    //             value={warehouseSearch}
                    //             name={"search"}
                    //             type={"text"}
                    //             required={false}
                    //             onChange={handleWarehouseSearch}
                    //             onInput={handleWarehouseDebouncedChange}
                    //             inputPlaceholder="Search warehouse"
                    //             containerClassname=""
                    //             inputContainerClassname={"!h-[40px] "}
                    //         />
                    //     </div>
                    // }
                    dropdown={
                        <div className="flex items-center justify-between lg:rounded-[5px] lg:shadow-buttonShadow2 py-[6px] px-[10px] gap-2 !border !border-slate-100">
                            <div
                                className={`text-slate-800 font-rocGroteskMedium text-[13px] inline-block whitespace-nowrap capitalize`}
                            >
                                {productFilter
                                    ? truncate(productFilter, {
                                          length: 12,
                                      })
                                    : "All products"}
                            </div>
                            <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>
                        </div>
                    }
                    dropdownItemsClasses="!px-0 !py-0"
                    dropdownClassName="!mb-10 !-mt-7"
                    dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 left-0 rounded-[5px] !max-h-[200px]
                                         !w-[200px] !min-w-min !pt-0
                            `}
                    name="valueRange"
                    dropdownOptions={[
                        {
                            label: (
                                <div
                                    className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                border-b border-b-slate-100 !w-[200px] px-4 py-[10px] capitalize"
                                >
                                    All products
                                </div>
                            ),
                            value: "",
                        },
                        ...(grouped?.productArray?.length > 0
                            ? grouped?.productArray?.map((item) => ({
                                  label: (
                                      <div
                                          className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                border-b border-b-slate-100 !w-[200px] px-4 py-[10px] capitalize"
                                      >
                                          {item}
                                      </div>
                                  ),
                                  value: item,
                              }))
                            : []),
                    ]}
                />
                <Dropdown
                    handleChange={(name, value) => setChannelFilter(value)}
                    dropdown={
                        <div className="flex items-center justify-between lg:rounded-[5px] lg:shadow-buttonShadow2 py-[6px] px-[10px] gap-2 !border !border-slate-100">
                            <div
                                className={`text-slate-800 font-rocGroteskMedium text-[13px] inline-block whitespace-nowrap capitalize`}
                            >
                                {channelFilter
                                    ? truncate(channelFilter, {
                                          length: 20,
                                      })
                                    : "All channels"}
                            </div>
                            <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>
                        </div>
                    }
                    dropdownItemsClasses="!px-0 !py-0"
                    dropdownClassName="!mb-10 !-mt-7"
                    dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[5px] !max-h-[200px]
                                         !w-[200px] !min-w-min !pt-0
                            `}
                    name="valueRange"
                    dropdownOptions={[
                        {
                            label: (
                                <div
                                    className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                border-b border-b-slate-100 !w-[200px] px-4 py-[10px] capitalize"
                                >
                                    All channels
                                </div>
                            ),
                            value: "",
                        },
                        ...(grouped?.channels?.length > 0
                            ? grouped?.channels?.map((item) => ({
                                  label: (
                                      <div
                                          className=" text-slate-600 font-rocGroteskMedium text-[13px] flex items-center gap-2
                                                border-b border-b-slate-100 !w-[200px] px-4 py-[10px] capitalize"
                                      >
                                          {item}
                                      </div>
                                  ),
                                  value: item,
                              }))
                            : []),
                    ]}
                />
                {targetSavedPerTable?.length === tableData2?.length ? (
                    <Button
                        btnText={"Target saved for all products"}
                        btnClassname="!bg-[#56B648] !w-fit !text-white !text-[13px] !whitespace-nowrap !px-2 !py-2.5 rounded-[5px] !h-[42px] !cursor-default"
                        icon={<i className="ri-check-double-fill text-base"></i>}
                    />
                ) : (
                    <SynthTooltip
                        divId={"save-target"}
                        position={"top"}
                        icon={
                            <Button
                                btnText={"Save target for all products"}
                                btnClassname="!bg-[#56B648] !w-fit !text-white !text-[13px] !whitespace-nowrap !px-2 !py-2.5 rounded-[5px] !h-[42px]"
                                icon={<i className="ri-add-line text-base"></i>}
                                isLoading={creatingForecast && Object.keys(savingId)?.length === 0}
                                type="button"
                                onClick={() => {
                                    setSavingId({});
                                    const targetToSave = tableData2.filter(
                                        (item) =>
                                            !targetSavedPerTable.some(
                                                (e) =>
                                                    e.inventoryId === item.inventoryId &&
                                                    e.channel === item.channel
                                            )
                                    );

                                    const body = targetToSave?.map((item) => ({
                                        inventoryId: item?.inventoryId,
                                        channel: item?.channel,
                                        forecastedUnits: item?.quantity,
                                        targetMonth: item?.date?.split("-")[1],
                                        targetYear: item?.date?.split("-")[0],
                                    }));
                                    handleCreateSalesTarget(body);
                                }}
                            />
                        }
                        content="Apply this sales target across all products in the current table."
                        styling={{
                            width: "270px",
                            background: "#142837",
                            borderRadius: "6px",
                        }}
                        contentClass="text-xs text-white"
                    />
                )}
            </div>

            <div className="mt-4 px-2 lg:px-5 overflow-x-clip">
                <TabsContainer
                    tabs={months}
                    activeTab={activeTab}
                    setActiveTab={(idx) => {
                        setActiveTab(idx);
                        const month = months?.length > 0 && months[idx >= 0 ? idx : 0];
                        setActiveMonth(month);
                    }}
                    component={
                        <div className="overflow-x-clip">
                            {tableData2?.length > 0 && (
                                <CustomTable
                                    tableBody={tableBody}
                                    tableHeader={tableHeader}
                                    isCellBordered={true}
                                    stickyHeader={true}
                                    headerContainerClass="!bg-slate-50"
                                    headerItemClass="font-rocGroteskMedium "
                                    tableClass="mb-4"
                                    isScrollable={true}
                                    scrollWithin={true}
                                    // headerContainerClass="!bg-slate-50 !border-slate-100"
                                    // tableContainerClass="!max-h-[200px] overflow-y-scroll"
                                />
                            )}
                        </div>
                    }
                    className="!px-0 overflow-x-auto relative"
                    showButtonClassName
                    itemClassName="!pb-2 max-lg:whitespace-nowrap !min-w-[70px]"
                    borderLineClase={"!text-slate-100 w-full mb-6"}
                    // tabContainerClassName=" "
                />
            </div>

            <div className="px-2 lg:px-5 flex items-center gap-2 border-t border-slate-200 pt-[15px]">
                <i className="ri-sparkling-fill text-sm text-[#0AAD0A] "></i>
                <p className="text-g-75 text-sm ">{summary}</p>
            </div>
        </div>
    );
};

export default SalesTargetTable;
